import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useRouter } from 'next/router';
import { normalizeText, urlAbsoluteToRelative3catinfo } from '@portal-internet/core';
import { getUrlFriendly } from 'components/3catinfo/Enllac/RouteResolver';
import { getRoutePathByCode } from 'utils/3catinfo/eleccionsHelper';
import { useEleccions } from 'hooks/3catinfo/useEleccions';
import { useViewport } from 'hooks/3catinfo/useViewport';
import Icona from 'components/3catinfo/ui/Icona';
import Enllac from 'components/3catinfo/Enllac';
import __isEmpty from 'lodash/isEmpty';
import clsx from 'clsx';
import styles from './cercadorEleccions.module.scss';

const getFormatItems = (items) => {
    return items?.map((item) => ({
        id: item.id,
        nom: item.nom,
        type: getRoutePathByCode(item.id),
        href: item.url
            ? urlAbsoluteToRelative3catinfo(item.url.replace('/324/', '/3catinfo/').replace('/3catinfo/eleccions/', '/3catinfo/'))
            : ''
    }));
};

const ItemResultatCerca = forwardRef((props, publicRef) => {
    const { item, index, handleKeyDown, onSelect } = props;
    const ref = useRef();
    const { nom } = item;

    useImperativeHandle(publicRef, () => ({
        focus() {
            ref.current.focus();
        }
    }));

    return (
        <li className={styles.itemResultat} ref={ref} tabIndex={index} onKeyDown={handleKeyDown}>
            <button type='button' className={styles.municipi} title={`Resultats a ${nom}`} onClick={() => onSelect(item)}>
                {nom}
            </button>
        </li>
    );
});

const Resultats = (props) => {
    const { items, searching, setSearching, selectedIndex, handleKeyDown, slug, onSelect } = props;
    const divRef = useRef(null);
    const [itemRefs, setItemRefs] = useState([]);

    const handleClickOutside = (event) => {
        if (divRef.current && !divRef.current.contains(event.target)) {
            setSearching(false);
        }
    };

    useEffect(() => {
        if (selectedIndex >= 0 && itemRefs[selectedIndex]) {
            itemRefs[selectedIndex].current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex]);

    useEffect(() => {
        setItemRefs((elRefs) =>
            Array(items.length)
                .fill()
                .map((_, i) => elRefs[i] || React.createRef())
        );
    }, [items]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!searching) return <></>;
    return (
        <div className={styles.wrapperResultats} ref={divRef}>
            <ul className={styles.contentResultats}>
                {__isEmpty(items) ? (
                    <li className={styles.noResults}>No s&apos;han trobat resultats</li>
                ) : (
                    items.map((item, index) => (
                        <ItemResultatCerca
                            key={`cercador-item-resultat-${item.id}`}
                            ref={itemRefs[index]}
                            slug={slug}
                            item={item}
                            index={index}
                            handleKeyDown={handleKeyDown}
                            onSelect={onSelect}
                        />
                    ))
                )}
            </ul>
        </div>
    );
};

const CercadorEleccions = (props) => {
    const { placeholder = 'Cerca municipi, província o CA', slug } = props;
    const items = getFormatItems(props.items || []);
    const router = useRouter();
    const { getSlugEleccions } = useEleccions();
    const [searchValue, setSearchValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [searching, setSearching] = useState(false);
    const updateCercaTimer = useRef(null);
    const inputRef = useRef(null);
    const debounceTimer = 300;
    const [isMouseFocus, setIsMouseFocus] = useState(false);
    const [isKeyboardFocus, setIsKeyboardFocus] = useState(false);
    const { zoomNavigator, isOnlyMobile } = useViewport();
    const placeholderText = isOnlyMobile
        ? placeholder === 'Cerca municipi, província o CA'
            ? 'Municipi, província o CA'
            : placeholder
        : placeholder;
    const stylesInnerSearcher = clsx({
        [styles.innerSearcher]: true,
        [styles.ambZoom]: zoomNavigator > 3
    });
    const stylesInput = clsx({
        [styles.searchInput]: true,
        [styles.searchInputActive]: isMouseFocus,
        [styles.searchInputFocus]: isKeyboardFocus
    });

    const getResultSearch = (value) => {
        return items.filter(({ nom }) => normalizeText(nom)?.toLowerCase().includes(normalizeText(value).toLowerCase()));
    };

    const resetSearch = (delay = 1000) => {
        clearTimeout(updateCercaTimer.current);
        const reset = () => {
            setSearchValue('');
            setSearchResults([]);
            setSearching(false);
            setSelectedIndex(-1);
        };
        setTimeout(reset, delay);
    };

    const navigateToResult = (item) => {
        resetSearch();

        const url = item.href
            ? item.href
            : getUrlFriendly({
                  routePath: item.type,
                  slug: slug || getSlugEleccions(),
                  slug2: item.nom,
                  id: item.id,
                  producte: 'web_noticies'
              });

        if (router.asPath !== url) {
            router.push(url).then(() => {
                setTimeout(() => {
                    document.getElementById('titol-plana')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }, 100);
            });
        }
    };

    const handleSelectResult = (item) => {
        navigateToResult(item);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const filterList = getResultSearch(searchValue);

        if (searchValue.length >= 2 && !__isEmpty(filterList)) {
            const selectedItem = filterList[selectedIndex === -1 ? 0 : selectedIndex];
            navigateToResult(selectedItem);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, searchResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            setSelectedIndex((prevIndex) => {
                if (prevIndex === 0) {
                    inputRef.current.focus();
                    requestAnimationFrame(() => {
                        const length = inputRef.current.value.length;
                        inputRef.current.setSelectionRange(length, length);
                    });
                }
                return Math.max(prevIndex - 1, -1);
            });
        } else if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    useEffect(() => {
        clearTimeout(updateCercaTimer.current);

        if (searchValue.length >= 2) {
            updateCercaTimer.current = setTimeout(() => {
                setSelectedIndex(-1);
                setSearchResults(getResultSearch(searchValue));
                setSearching(true);
            }, debounceTimer);
        } else {
            setSearching(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchValue]);

    return (
        <div className={stylesInnerSearcher}>
            <form onSubmit={handleSubmit} className={styles.searchForm}>
                <div className={styles.contentInput}>
                    <label htmlFor='search' className='sr-only'>
                        Buscar:
                    </label>
                    <input
                        className={stylesInput}
                        onMouseDown={() => {
                            setIsMouseFocus(true);
                            setIsKeyboardFocus(false);
                        }}
                        onBlur={() => {
                            setIsMouseFocus(false);
                            setIsKeyboardFocus(false);
                        }}
                        onKeyDown={handleKeyDown}
                        onKeyUp={(e) => {
                            if (e.key === 'Tab' || (e.shiftKey && e.key === 'Tab')) {
                                setIsMouseFocus(false);
                            }
                        }}
                        onFocus={(e) => {
                            if (!isMouseFocus) {
                                setIsKeyboardFocus(true);
                            }
                        }}
                        ref={inputRef}
                        id='search'
                        type='search'
                        name='search'
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder={placeholderText}
                        required
                        aria-label={placeholderText}
                        autoComplete='off'
                        aria-haspopup='listbox'
                        aria-autocomplete='list'
                    />
                    <button type='submit' className={styles.searchButton} aria-label='buscar'>
                        <Icona className={styles.icona} icona='cercador_ple' isBlanc={false} inline={true} />
                    </button>
                </div>
                <Resultats
                    searching={searching}
                    searchValue={searchValue}
                    items={searchResults}
                    selectedIndex={selectedIndex}
                    setSearching={setSearching}
                    handleKeyDown={handleKeyDown}
                    onSelect={handleSelectResult}
                    slug={slug}
                />
            </form>
        </div>
    );
};

export default CercadorEleccions;

ItemResultatCerca.displayName = 'ItemResultatCerca';
